import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, UserPlus, Edit3, Trash2 } from "lucide-react";
import { useLookups } from "@/context/LookupContext";
import { getErrorMessage } from "@/lib/errorUtils";
import { norm } from "@/lib/normalize";

const generatePassword = (firstName) => {
  const prefix = (firstName || "").slice(0, 4).charAt(0).toUpperCase() + (firstName || "").slice(1, 4).toLowerCase();
  return `${prefix}SOS@26`;
};

export default function Users() {
  const { lookups } = useLookups();
  const [staff, setStaff] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ staffId: "", roleId: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);

  const loadUsers = () => api.get("/auth/users").then((r) => setUsers(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data)).catch(() => {});
    loadUsers();
  }, []);

  const roles = lookups?.roles || [];

  const onStaffChange = (staffId) => {
    const s = staff.find((x) => String(x.id) === staffId);
    const phone = s?.phone || "";
    const password = generatePassword(s?.firstName || "");
    setForm({ ...form, staffId, phone, password });
  };

  const openCreate = () => {
    setEditId(null);
    setForm({ staffId: "", roleId: "", phone: "", password: "" });
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ staffId: u.staffId ? String(u.staffId) : "", roleId: u.roleId ? String(u.roleId) : "", phone: u.phone || "", password: "" });
    setOpen(true);
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user account?")) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success("User deleted");
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete user"));
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!editId && (!form.staffId || !form.roleId || !form.phone)) {
      toast.error("Staff, role, and phone are required");
      return;
    }
    if (editId && !form.roleId) {
      toast.error("Role is required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.put(`/auth/users/set-role`, {
          roleId: form.roleId,
          phone: form.phone,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success("User updated");
      } else {
        const selectedStaff = staff.find((x) => String(x.id) === form.staffId);
        await api.post("/auth/register", {
          staffId: form.staffId,
          name: `${selectedStaff?.firstName || ""} ${selectedStaff?.lastName || ""}`.trim(),
          phone: form.phone,
          password: form.password,
          roleId: form.roleId,
        });
        toast.success("User created");
      }
      setOpen(false);
      setForm({ staffId: "", roleId: "", phone: "", password: "" });
      setEditId(null);
      loadUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="users-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="overline mb-2">Team</div>
          <h1 className="font-heading text-4xl text-white">User Accounts</h1>
          <p className="text-emerald-muted mt-2 text-sm">Create login credentials for staff members.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button onClick={openCreate} data-testid="add-user-btn"
              className="gold-button px-5 py-2.5 rounded-full inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Create User
            </button>
          </DialogTrigger>
          <DialogContent className="bg-emerald-surface border-gold-subtle text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-gold" /> {editId ? "Edit User" : "Create User Login"}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Staff Member</Label>
                <Select value={form.staffId} onValueChange={onStaffChange} disabled={!!editId}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-10" data-testid="user-staff-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.firstName} {s.lastName || ""} — {s.designation}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.roleId} onValueChange={(val) => setForm({ ...form, roleId: val })}>
                  <SelectTrigger className="mt-1 bg-emerald-elev border-gold-subtle text-white h-10" data-testid="user-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={String(r.id)}>{r.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="user-phone-input" required />
              </div>
              <div>
                <Label>Password {editId && <span className="text-emerald-muted">(leave blank to keep current)</span>}</Label>
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="bg-emerald-elev border-gold-subtle mt-1" data-testid="user-password-input" required={!editId} />
                {!editId && <p className="text-xs text-emerald-muted mt-1">Default: first 4 letters of name + SOS@26</p>}
              </div>
              <button type="submit" disabled={saving}
                className="gold-button w-full h-11 rounded-md disabled:opacity-60" data-testid="save-user-btn">
                {saving ? "Saving…" : (editId ? "Save Changes" : "Create User")}
              </button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-emerald-surface border border-gold-subtle rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-gold-subtle hover:bg-transparent">
              <TableHead className="text-emerald-muted">Name</TableHead>
              <TableHead className="text-emerald-muted">Phone</TableHead>
              <TableHead className="text-emerald-muted">Role</TableHead>
              <TableHead className="text-emerald-muted w-24"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center text-emerald-muted py-8">No user accounts yet.</TableCell></TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id} className="border-gold-subtle" data-testid={`user-row-${u.id}`}>
                <TableCell className="text-white">{u.firstName || u.name} {u.lastName || ""}</TableCell>
                <TableCell className="text-emerald-muted">{u.phone || "—"}</TableCell>
                <TableCell className="text-emerald-muted">{norm(u.roleName) || "—"}</TableCell>
                <TableCell className="text-right">
                  <button onClick={() => openEdit(u)} className="text-emerald-muted hover:text-gold p-1" data-testid={`edit-user-${u.id}`}><Edit3 className="w-4 h-4" /></button>
                  <button onClick={() => del(u.id)} className="text-emerald-muted hover:text-red-300 p-1 ml-2" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
